import React, { useEffect, useRef, useCallback } from 'react';
import styled from '@emotion/styled';
import mapboxgl from 'mapbox-gl';
import { ENV } from '@/utils/env';
import 'mapbox-gl/dist/mapbox-gl.css';

type MapPreviewProps = {
    lat: number;
    lng: number;
    zoom?: number;
};

const MapContainer = styled.div`
    width: 100%;
    height: 248px;
    border-radius: ${({ theme }) => theme.radius.main};
    overflow: hidden;
    border: 1px solid ${({ theme }) => theme.color.grey200};
`;

const Placeholder = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => theme.color.grey100};
    color: ${({ theme }) => theme.color.black200};
    font-size: ${({ theme }) => theme.font.size.small};
    z-index: 1;
`;

const Wrapper = styled.div`
    position: relative;
`;

export const MapPreview: React.FunctionComponent<MapPreviewProps> = ({ lat, lng, zoom = 12 }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const markerRef = useRef<mapboxgl.Marker | null>(null);

    const hasCoords = Boolean(lat || lng);
    const hasToken = Boolean(ENV.REACT_APP_MAPBOX_API_KEY);

    const initMap = useCallback(() => {
        if (!containerRef.current || mapRef.current || !ENV.REACT_APP_MAPBOX_API_KEY) return;

        mapboxgl.accessToken = ENV.REACT_APP_MAPBOX_API_KEY;

        mapRef.current = new mapboxgl.Map({
            container: containerRef.current,
            style: 'mapbox://styles/mapbox/streets-v11',
            center: [lng, lat],
            zoom,
        });
        mapRef.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
    }, [lat, lng, zoom]);

    useEffect(() => {
        return () => {
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
                markerRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (!hasCoords) return;

        if (!mapRef.current) {
            initMap();
        }

        if (!mapRef.current) return;

        if (markerRef.current) {
            markerRef.current.remove();
        }

        markerRef.current = new mapboxgl.Marker({ color: '#e74c3c' }).setLngLat([lng, lat]).addTo(mapRef.current);
        mapRef.current.jumpTo({ center: [lng, lat], zoom });
    }, [lat, lng, zoom, hasCoords, initMap]);

    if (!hasToken) {
        return (
            <Wrapper>
                <MapContainer />
                <Placeholder>Map unavailable</Placeholder>
            </Wrapper>
        );
    }

    return (
        <Wrapper>
            <MapContainer ref={containerRef} />
            {!hasCoords && <Placeholder>No location</Placeholder>}
        </Wrapper>
    );
};
